import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook for managing insight carousel state and auto-rotation
 * 
 * @param {number} insightCount - Total number of insights
 * @param {number} autoRotateInterval - Interval in ms (default: 5000)
 * @param {boolean} autoRotate - Whether to auto-rotate (default: true)
 * @returns {Object} Carousel state and controls
 */
export function useInsightCarousel(insightCount, autoRotateInterval = 5000, autoRotate = true) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const intervalRef = useRef(null);

  // Reset index when insight count changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [insightCount]);

  // Auto-rotation logic
  useEffect(() => {
    if (!autoRotate || insightCount <= 1 || isPaused || isTouching) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % insightCount);
    }, autoRotateInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [insightCount, autoRotateInterval, autoRotate, isPaused, isTouching]);

  const goToNext = useCallback(() => {
    if (insightCount > 0) {
      setCurrentIndex((prev) => (prev + 1) % insightCount);
    }
  }, [insightCount]);

  const goToPrevious = useCallback(() => {
    if (insightCount > 0) {
      setCurrentIndex((prev) => (prev - 1 + insightCount) % insightCount);
    }
  }, [insightCount]);

  const goToIndex = useCallback((index) => {
    if (index >= 0 && index < insightCount) {
      setCurrentIndex(index);
    }
  }, [insightCount]);

  const pause = useCallback(() => {
    setIsPaused(true);
  }, []);

  const resume = useCallback(() => {
    setIsPaused(false);
  }, []);

  const handleTouchStart = useCallback(() => {
    setIsTouching(true);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsTouching(false);
  }, []);

  return {
    currentIndex,
    goToNext,
    goToPrevious,
    goToIndex,
    pause,
    resume,
    isPaused,
    handleTouchStart,
    handleTouchEnd,
    canGoNext: insightCount > 1,
    canGoPrevious: insightCount > 1,
    totalCount: insightCount,
  };
}
